/**
 * BIMI Section
 *
 * Extracted from domain detail page for use in the Configuration tab.
 * Manages BIMI readiness checking, logo validation, DNS record generation, and VMC guidance.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import {
  CheckCircle2,
  XCircle,
  HelpCircle,
  RefreshCw,
  Copy,
  BadgeCheck,
  ExternalLink,
  Sparkles,
} from "lucide-react";
import {
  api,
  Domain,
  BimiReadinessResponse,
  BimiLogoValidationResult,
  BimiRecordGeneratorResponse,
} from "@/lib/api";

interface BimiSectionProps {
  domain: Domain;
  domainId: string;
  tier: string;
}

export function BimiSection({ domain, domainId, tier }: BimiSectionProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [readiness, setReadiness] = useState<BimiReadinessResponse | null>(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [vmcUrl, setVmcUrl] = useState("");
  const [selector, setSelector] = useState("default");
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<BimiLogoValidationResult | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generatedRecord, setGeneratedRecord] = useState<BimiRecordGeneratorResponse | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  // Determine if user has full BIMI access (Pro+) or just teaser (Starter)
  const hasFullAccess = tier === "pro" || tier === "enterprise" || tier === "enterprise_plus";
  const currentPolicy = domain.current_policy || domain.dmarc_policy;
  const isAtEnforcement = currentPolicy === "reject";

  useEffect(() => {
    checkReadiness();
  }, [domainId]);

  async function checkReadiness() {
    try {
      setLoading(true);
      const result = await api.getBimiReadiness(domainId);
      setReadiness(result);
    } catch (error) {
      console.error("Failed to check BIMI readiness:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleValidateLogo() {
    if (!logoUrl.trim()) return;
    try {
      setValidating(true);
      setValidationResult(null);
      const result = await api.validateBimiLogo(domainId, logoUrl.trim());
      setValidationResult(result);
    } catch (error) {
      console.error("Failed to validate logo:", error);
      toast.error("Failed to validate BIMI logo");
    } finally {
      setValidating(false);
    }
  }

  async function handleGenerateRecord() {
    if (!logoUrl.trim()) return;
    try {
      setGenerating(true);
      const result = await api.generateBimiRecord(domainId, {
        logo_url: logoUrl.trim(),
        vmc_url: vmcUrl.trim() || undefined,
        selector: selector.trim() || "default",
      });
      setGeneratedRecord(result);
    } catch (error) {
      console.error("Failed to generate BIMI record:", error);
      toast.error("Failed to generate BIMI record");
    } finally {
      setGenerating(false);
    }
  }

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Starter tier teaser view
  if (!hasFullAccess) {
    return (
      <div className="space-y-6">
        {/* Celebration Banner */}
        {isAtEnforcement && (
          <div className="bg-slate-50 border-l-4 border-amber-500 p-6 rounded-r-xl">
            <div className="flex items-start gap-4">
              <Sparkles className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-slate-800">
                  You've reached enforcement!
                </h3>
                <p className="text-slate-600 mt-1">
                  Now get your logo in Gmail, Yahoo, and Apple Mail with BIMI.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Read-only Readiness Checklist */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">
            BIMI Readiness Checklist
          </h3>
          <div className="space-y-3">
            {readiness?.checks.map((check) => (
              <div key={check.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex-shrink-0 mt-0.5">
                  {check.status === "pass" ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                  ) : check.status === "fail" ? (
                    <XCircle className="w-5 h-5 text-red-600" />
                  ) : (
                    <HelpCircle className="w-5 h-5 text-slate-400" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-slate-800">{check.label}</p>
                  <p className="text-sm text-slate-500">{check.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upgrade CTA */}
        <div className="bg-slate-800 rounded-xl p-6 text-center">
          <BadgeCheck className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            Upgrade to Pro
          </h3>
          <p className="text-slate-300 mb-4">
            Validate your logo and generate your BIMI DNS record with Pro.
          </p>
          <button
            onClick={() => navigate("/settings/billing")}
            className="px-6 py-2.5 bg-amber-500 text-slate-900 font-medium rounded-lg hover:bg-amber-400 transition-colors"
          >
            Upgrade to Pro
          </button>
        </div>
      </div>
    );
  }

  // Pro+ full BIMI view
  return (
    <div className="space-y-6">
      {/* Readiness Check Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800">
            BIMI Readiness
          </h3>
          <button
            onClick={checkReadiness}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Check Readiness
          </button>
        </div>

        {readiness && (
          <>
            {/* Score */}
            <div className="flex items-center gap-4 mb-4">
              <div className="relative w-16 h-16">
                <svg className="w-16 h-16 transform -rotate-90">
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    className="text-slate-200"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${(readiness.score / 100) * 176} 176`}
                    className="text-amber-500"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-slate-800">
                  {readiness.score}%
                </span>
              </div>
              <div>
                <p className="font-medium text-slate-800">
                  {readiness.is_ready ? "Ready for BIMI" : "Getting Ready"}
                </p>
                <p className="text-sm text-slate-500">
                  {readiness.checks.filter(c => c.passed).length} of {readiness.checks.length} checks passed
                </p>
              </div>
            </div>

            {/* Checklist */}
            <div className="space-y-2">
              {readiness.checks.map((check) => (
                <div key={check.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="flex-shrink-0 mt-0.5">
                    {check.status === "pass" ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                    ) : check.status === "fail" ? (
                      <XCircle className="w-5 h-5 text-red-600" />
                    ) : (
                      <HelpCircle className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{check.label}</p>
                    <p className="text-sm text-slate-500">{check.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Logo Validation Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">
          Logo Validation
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          Enter your logo URL to validate it meets BIMI requirements.
        </p>

        <div className="flex gap-3 mb-4">
          <input
            type="url"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://example.com/logo.svg"
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          />
          <button
            onClick={handleValidateLogo}
            disabled={validating || !logoUrl.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-medium rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {validating ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            Validate
          </button>
        </div>

        {validationResult && (
          <div className="space-y-3">
            <div className={`p-4 rounded-lg ${validationResult.is_valid ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`}>
              <p className={`font-medium ${validationResult.is_valid ? "text-emerald-700" : "text-red-700"}`}>
                {validationResult.is_valid ? "Logo is valid for BIMI" : "Logo has issues"}
              </p>
            </div>

            <div className="space-y-2">
              {validationResult.checks.map((check, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  {check.passed ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  )}
                  <div>
                    <p className="font-medium text-slate-800">{check.name}</p>
                    <p className="text-sm text-slate-500">{check.message}</p>
                  </div>
                </div>
              ))}
            </div>

            {validationResult.preview_available && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-2">Logo Preview</p>
                <div className="w-24 h-24 bg-white border border-slate-200 rounded-lg flex items-center justify-center overflow-hidden">
                  <img
                    src={validationResult.logo_url}
                    alt="BIMI Logo Preview"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* DNS Record Generator Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">
          DNS Record Generator
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          Generate your BIMI DNS record to add to your domain.
        </p>

        <div className="space-y-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Logo URL <span className="text-red-600">*</span>
            </label>
            <input
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://example.com/logo.svg"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              VMC Certificate URL <span className="text-slate-400">(optional)</span>
            </label>
            <input
              type="url"
              value={vmcUrl}
              onChange={(e) => setVmcUrl(e.target.value)}
              placeholder="https://example.com/vmc.pem"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
            <p className="text-xs text-slate-500 mt-1">
              Required for Gmail. Get a VMC from DigiCert or Entrust.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Selector
            </label>
            <input
              type="text"
              value={selector}
              onChange={(e) => setSelector(e.target.value.toLowerCase())}
              placeholder="default"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
            <p className="text-xs text-slate-500 mt-1">
              Usually "default". Use different selectors for multiple logos.
            </p>
          </div>
        </div>

        <button
          onClick={handleGenerateRecord}
          disabled={generating || !logoUrl.trim()}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-medium rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {generating ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <BadgeCheck className="w-4 h-4" />
          )}
          Generate Record
        </button>

        {generatedRecord && (
          <div className="mt-6 space-y-4">
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="font-medium text-emerald-700">
                BIMI record generated successfully!
              </p>
              <p className="text-sm text-emerald-700 mt-1">
                Add this TXT record to your DNS provider.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-slate-700">Host / Name</label>
                  <button
                    onClick={() => copyToClipboard(generatedRecord.host, "host")}
                    className="text-sm text-amber-600 hover:text-amber-700 flex items-center gap-1"
                  >
                    {copied === "host" ? (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <code className="block p-3 bg-slate-100 border border-slate-200 rounded-lg text-sm font-mono overflow-x-auto">
                  {generatedRecord.host}
                </code>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-slate-700">Value</label>
                  <button
                    onClick={() => copyToClipboard(generatedRecord.value, "value")}
                    className="text-sm text-amber-600 hover:text-amber-700 flex items-center gap-1"
                  >
                    {copied === "value" ? (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <code className="block p-3 bg-slate-100 border border-slate-200 rounded-lg text-sm font-mono overflow-x-auto break-all">
                  {generatedRecord.value}
                </code>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Record Type</label>
                <code className="block p-3 bg-slate-100 border border-slate-200 rounded-lg text-sm font-mono mt-1">
                  {generatedRecord.record_type}
                </code>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* VMC Guidance Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">
          VMC Certificate
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          A Verified Mark Certificate (VMC) is required for your logo to display in Gmail.
          Yahoo and Apple Mail may show logos without a VMC.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <a
            href="https://www.digicert.com/tls-ssl/verified-mark-certificates"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <div className="flex-1">
              <p className="font-medium text-slate-800">DigiCert</p>
              <p className="text-sm text-slate-500">Industry-leading VMC provider</p>
            </div>
            <ExternalLink className="w-5 h-5 text-slate-400" />
          </a>

          <a
            href="https://www.entrust.com/digital-security/certificate-solutions/products/digital-signing/verified-mark-certificates"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <div className="flex-1">
              <p className="font-medium text-slate-800">Entrust</p>
              <p className="text-sm text-slate-500">Trusted VMC authority</p>
            </div>
            <ExternalLink className="w-5 h-5 text-slate-400" />
          </a>
        </div>

        {tier === "pro" && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>Enterprise includes:</strong> VMC monitoring, logo hosting, and setup wizard.{" "}
              <button
                onClick={() => navigate("/settings/billing")}
                className="text-amber-600 hover:text-amber-700 underline"
              >
                Upgrade to Enterprise
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
